import { useCallback, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import type { QueueItem } from '../lib/postTypes'
import { useFormats, useQueue } from '../lib/queries'
import { splitQueue } from '../lib/queueRules'
import { Notice, QueryState } from '../components/QueryState'
import { ApprovalCard } from './queue/ApprovalCard'
import { FailedCard } from './queue/FailedCard'
import { StockSection } from './queue/StockSection'
import { NotedCard } from './queue/NotedCard'

const SORTS = [
  { key: 'newest', label: '新しい順' },
  { key: 'oldest', label: '古い順' },
  { key: 'score', label: 'スコア高い順' },
  { key: 'length', label: '文字数少ない順' },
] as const
type SortKey = (typeof SORTS)[number]['key']

function itemText(item: QueueItem): string {
  return item.activeVersion?.variants.x?.text ?? ''
}

export function Queue() {
  // 生成中の投稿が承認可能になったら自動で現れるように（audit M-6）
  const queue = useQueue({ refetchInterval: 5000 })
  // 操作成功のお知らせ（承認するとカードが別セクションへ移るため、ページ上部に出す）
  const [flash, setFlash] = useState<string | null>(null)
  const notify = useCallback((m: string) => setFlash(m), [])

  // ダッシュボードの「予約済み」から /queue#stock で来たらその位置へ
  const { hash } = useLocation()
  const loaded = queue.data !== undefined
  useEffect(() => {
    if (loaded && hash) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' })
  }, [loaded, hash])

  return (
    <div className="space-y-4">
      {flash && <Notice kind="success" onClose={() => setFlash(null)}>{flash}</Notice>}
      <QueryState query={queue} label="承認キュー">
        {data => <QueueBody items={data} notify={notify} />}
      </QueryState>
    </div>
  )
}

function QueueBody({ items, notify }: { items: QueueItem[]; notify: (m: string) => void }) {
  const formats = useFormats()
  const [q, setQ] = useState('')
  const [kindFilter, setKindFilter] = useState('all')
  const [formatFilter, setFormatFilter] = useState('all')
  const [guardFilter, setGuardFilter] = useState('all')
  const [imageFilter, setImageFilter] = useState('all')
  const [sort, setSort] = useState<SortKey>('newest')

  // 生成中・失敗・ストック等はセクションを分けて表示（従来はどの画面にも出ず「投稿が消えた」ように見えた — audit M-6/C1）
  const { working, failed, approval, stock, publishing, noted } = splitQueue(items)

  const query = q.trim().toLowerCase()
  const filtered = approval
    .filter(item => {
      if (kindFilter !== 'all' && item.post.kind !== kindFilter) return false
      if (formatFilter !== 'all' && item.activeVersion?.brief?.format !== formatFilter) return false
      if (guardFilter !== 'all' && !item.channels.some(c => c.status === guardFilter)) return false
      const hasImage = !!item.activeVersion?.variants.x?.image
      if (imageFilter === 'with' && !hasImage) return false
      if (imageFilter === 'without' && hasImage) return false
      if (query && !(item.post.title + itemText(item)).toLowerCase().includes(query)) return false
      return true
    })
    .sort((a, b) => {
      if (sort === 'newest') return b.post.createdAt.localeCompare(a.post.createdAt)
      if (sort === 'oldest') return a.post.createdAt.localeCompare(b.post.createdAt)
      if (sort === 'score') return (b.activeVersion?.score ?? -1) - (a.activeVersion?.score ?? -1)
      return itemText(a).length - itemText(b).length
    })

  return (
    <div className="space-y-4">
      <h1 id="queue-heading" tabIndex={-1} className="text-2xl font-bold leading-snug outline-none">承認キュー（<span className="gm-num">{approval.length}</span>件）</h1>

      {/* AI が生成中（グラデの輪は生成ジョブの進行中だけに使う） */}
      {working.length > 0 && (
        <div role="status" className="gm-card flex items-center gap-2 p-3 text-sm">
          <span className="gm-ai-ring" aria-hidden />
          <span><span className="gm-num">{working.length}</span>件の投稿を生成中です（完了するとこの下に現れます）</span>
        </div>
      )}

      {/* 投稿は済んだが、確かめてほしいことがある（投稿済みの投稿はふつうここに出ないので、注意があるものだけ出す） */}
      {noted.map(item => <NotedCard key={item.post.id} item={item} notify={notify} />)}

      {failed.map(item => <FailedCard key={item.post.id} item={item} notify={notify} />)}

      {approval.length === 0 ? (
        working.length === 0 && failed.length === 0 && noted.length === 0 && (
          <p className="text-sm text-ink-muted">
            承認待ちの投稿はありません。<Link to="/compose" className="font-bold text-primary underline">投稿を作る</Link>から新しい投稿を生成できます。
          </p>
        )
      ) : (
        <>
          {/* 検索・フィルター・ソート（承認待ちに対して効く） */}
          <div className="gm-card flex flex-wrap items-center gap-2 p-3 text-sm">
            <input value={q} onChange={e => setQ(e.target.value)} data-testid="queue-search"
              placeholder="タイトル・本文を検索" aria-label="タイトル・本文を検索"
              className="gm-input min-w-52 flex-1" />
            <select value={kindFilter} onChange={e => setKindFilter(e.target.value)} aria-label="種別" className="gm-input w-auto">
              <option value="all">種別: すべて</option>
              <option value="tips">お役立ち</option>
              <option value="product">商品紹介</option>
            </select>
            <select value={formatFilter} onChange={e => setFormatFilter(e.target.value)} aria-label="型" className="gm-input w-auto">
              <option value="all">型: すべて</option>
              {(formats.data ?? []).map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
            <select value={guardFilter} onChange={e => setGuardFilter(e.target.value)} aria-label="安全チェックで絞り込み" className="gm-input w-auto">
              <option value="all">安全チェック: すべて</option>
              <option value="guard_ok">安全チェック済み</option>
              <option value="gated_out">要確認</option>
              <option value="blocked">投稿不可</option>
            </select>
            <select value={imageFilter} onChange={e => setImageFilter(e.target.value)} aria-label="画像" className="gm-input w-auto">
              <option value="all">画像: すべて</option>
              <option value="with">画像あり</option>
              <option value="without">画像なし</option>
            </select>
            <select value={sort} onChange={e => setSort(e.target.value as SortKey)} aria-label="並び順" className="gm-input w-auto">
              {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            {/* 承認待ち件数と比べる（従来は生成中・失敗も含む全件と比べていた） */}
            {filtered.length !== approval.length && (
              <span className="text-xs text-ink-muted"><span className="gm-num">{approval.length}</span>件中 <span className="gm-num">{filtered.length}</span>件を表示</span>
            )}
          </div>

          {filtered.length === 0 && <p className="text-sm text-ink-muted">条件に合う投稿がありません。検索語や絞り込みを変えてください。</p>}
          {filtered.map(item => <ApprovalCard key={item.post.id} item={item} notify={notify} />)}
        </>
      )}

      <div className="pt-4">
        <StockSection stock={stock} publishing={publishing} notify={notify} />
      </div>
    </div>
  )
}
